/**
 * Non-destructive raw adjustments, and the sidecar they are stored in.
 *
 * The original is never touched. A raw file is sensor data, and rewriting one
 * loses the thing that makes it worth keeping; every editor worth using stores
 * its work beside the file instead. This does the same, in Camera Raw's own
 * `crs:` vocabulary, so Lightroom, Bridge and darktable read the result as a
 * starting point rather than as a foreign format.
 *
 * One caveat is worth stating plainly rather than discovering: matching those
 * field names does not make the *rendering* match. Adobe's process version is
 * proprietary and ours is LibRaw plus arithmetic, so an image opened in
 * Lightroom will resemble what the archive shows without being identical. The
 * numbers are portable; the pixels are an interpretation.
 *
 * Pure by design. Generating a sidecar that names the wrong source file, or
 * writing values a tool will silently clamp, is the sort of thing that only
 * surfaces once someone opens it a year later.
 */

export interface RawEdit {
  /** Exposure in stops. Camera Raw's own range. */
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  whites: number;
  blacks: number;
  /** Kelvin. 0 means "leave the camera's own white balance alone". */
  temperature: number;
  tint: number;
  vibrance: number;
  saturation: number;
}

export const NEUTRAL_EDIT: RawEdit = {
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
  temperature: 0,
  tint: 0,
  vibrance: 0,
  saturation: 0,
};

/** Bounds, matching Camera Raw so exported values mean the same thing there. */
export const EDIT_RANGES: Record<keyof RawEdit, { min: number; max: number }> = {
  exposure: { min: -5, max: 5 },
  contrast: { min: -100, max: 100 },
  highlights: { min: -100, max: 100 },
  shadows: { min: -100, max: 100 },
  whites: { min: -100, max: 100 },
  blacks: { min: -100, max: 100 },
  temperature: { min: 0, max: 50000 },
  tint: { min: -150, max: 150 },
  vibrance: { min: -100, max: 100 },
  saturation: { min: -100, max: 100 },
};

/**
 * Forces an edit into range.
 *
 * Clamped rather than rejected: a slider that has drifted a fraction past its
 * bound is not worth failing a save over, and a value outside Camera Raw's
 * range would be silently clamped by any tool reading the sidecar anyway --
 * better to write what will actually be read.
 */
export function clampEdit(input: Partial<RawEdit>): RawEdit {
  const out = { ...NEUTRAL_EDIT };

  for (const key of Object.keys(NEUTRAL_EDIT) as Array<keyof RawEdit>) {
    const value = input[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const { min, max } = EDIT_RANGES[key];
    out[key] = Math.min(max, Math.max(min, value));
  }

  return out;
}

/** True when nothing would change, so no rendition is worth producing. */
export function isNeutral(edit: RawEdit): boolean {
  return (Object.keys(NEUTRAL_EDIT) as Array<keyof RawEdit>).every(
    (key) => edit[key] === NEUTRAL_EDIT[key],
  );
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Camera Raw writes signed decimals for the 2012 process controls. */
function signed(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2);
}

/**
 * Builds the XMP sidecar for an edit.
 *
 * crs:RawFileName is not decoration. It is the field this archive's own merge
 * checks a sidecar against, so writing it means a sidecar that ends up beside
 * the wrong file is caught by the same rule that protects everything else --
 * including if it is this code that misplaced it.
 */
export function buildEditXmp(options: {
  edit: RawEdit;
  sourceFilename: string;
  editedAt?: Date;
  editedBy?: string;
}): string {
  const { edit, sourceFilename } = options;
  const when = (options.editedAt ?? new Date()).toISOString();

  // Temperature of 0 means the camera's own white balance, which is expressed
  // by omitting the field rather than by writing a zero Kelvin nobody can use.
  const whiteBalance =
    edit.temperature > 0
      ? `\n   crs:WhiteBalance="Custom"\n   crs:Temperature="${Math.round(edit.temperature)}"\n   crs:Tint="${Math.round(edit.tint)}"`
      : `\n   crs:WhiteBalance="As Shot"`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
 <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
  <rdf:Description rdf:about=""
   xmlns:crs="http://ns.adobe.com/camera-raw-settings/1.0/"
   xmlns:xmp="http://ns.adobe.com/xap/1.0/"
   crs:RawFileName="${escapeXml(sourceFilename)}"
   crs:Version="15.0"
   crs:ProcessVersion="11.0"${whiteBalance}
   crs:Exposure2012="${signed(edit.exposure)}"
   crs:Contrast2012="${Math.round(edit.contrast)}"
   crs:Highlights2012="${Math.round(edit.highlights)}"
   crs:Shadows2012="${Math.round(edit.shadows)}"
   crs:Whites2012="${Math.round(edit.whites)}"
   crs:Blacks2012="${Math.round(edit.blacks)}"
   crs:Vibrance="${Math.round(edit.vibrance)}"
   crs:Saturation="${Math.round(edit.saturation)}"
   xmp:ModifyDate="${when}"
   xmp:CreatorTool="Team Media Archive"${
     options.editedBy ? `\n   xmp:MetadataDate="${when}"` : ''
   }/>
 </rdf:RDF>
</x:xmpmeta>
`;
}

/**
 * CSS filter approximating an edit, for the live preview.
 *
 * The browser applies this to the already-developed JPEG so the sliders
 * respond immediately. It is an approximation and is meant to be: the real
 * render happens on the server from the sensor data, where highlight recovery
 * and white balance actually mean something. Exposure recovered from a JPEG's
 * clipped highlights cannot bring back what the JPEG never contained.
 *
 * Kept here rather than in the component so the mapping is in the same file as
 * the values it interprets.
 */
export function editToCssFilter(edit: RawEdit): string {
  const parts: string[] = [];

  // A stop is a doubling, and CSS brightness is linear, so exposure maps
  // through a power of two rather than proportionally.
  if (edit.exposure !== 0) parts.push(`brightness(${(2 ** edit.exposure).toFixed(3)})`);
  if (edit.contrast !== 0) parts.push(`contrast(${(1 + edit.contrast / 200).toFixed(3)})`);

  const saturationTotal = edit.saturation + edit.vibrance * 0.5;
  if (saturationTotal !== 0) {
    parts.push(`saturate(${Math.max(0, 1 + saturationTotal / 100).toFixed(3)})`);
  }

  // Shadows and blacks both lift the low end; approximated together, since CSS
  // has no tonal-range control and pretending otherwise would mislead.
  const lift = edit.shadows + edit.blacks;
  if (lift !== 0) parts.push(`brightness(${(1 + lift / 400).toFixed(3)})`);

  if (edit.temperature > 0) {
    // Warmer than daylight leans amber, cooler leans blue. sepia plus a hue
    // rotation is the closest CSS gets without a canvas pass.
    const warmth = Math.max(-1, Math.min(1, (edit.temperature - 5500) / 4500));
    if (warmth !== 0) {
      parts.push(`sepia(${Math.abs(warmth * 0.35).toFixed(3)})`);
      parts.push(`hue-rotate(${warmth > 0 ? 0 : -30}deg)`);
    }
  }

  return parts.length > 0 ? parts.join(' ') : 'none';
}
